import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Mail, 
  Monitor, 
  Shield, 
  Cloud, 
  Network, 
  Headphones, 
  Cpu,
  CheckCircle2,
  ArrowRight,
  ShoppingBag,
  Home
} from 'lucide-react';

const coreServices = [
  {
    icon: Monitor,
    title: 'Hardware and Software Sales',
    description: 'Workstations, servers, printers, supplies, UPS, licensing, and procurement.'
  },
  {
    icon: Headphones,
    title: 'Managed IT',
    description: 'Help desk support, monitoring, patching, standards, and documentation.'
  },
  {
    icon: Shield,
    title: 'Cybersecurity',
    description: 'Endpoint protection, email security, identity controls, and remediation.'
  },
  {
    icon: Cloud,
    title: 'Microsoft 365 and Azure',
    description: 'Administration, licensing, migrations, and cloud setup.'
  },
  {
    icon: Network,
    title: 'Networks',
    description: 'Firewalls, switching, Wi-Fi, VPNs, and site connectivity.'
  },
  {
    icon: Phone,
    title: 'Voice and Unified Communications',
    description: 'VoIP systems, call routing, and user support.'
  },
  {
    icon: Cpu,
    title: 'AI and Automation',
    description: 'OCR extraction, summarization workflows, and internal automation.'
  },
  {
    icon: ShoppingBag,
    title: 'One Stop Shopping',
    description: 'Bundled monthly plans for new businesses covering hardware, software, networking, cloud, and support.'
  },
  {
    icon: Home,
    title: 'Home automation and surveillance systems',
    description: 'Bring comfort, control, and peace of mind together with smart home, AV, access control, and surveillance solutions from trusted leading brands.'
  }
];

const benefits = [
  'Predictable support and faster issue resolution',
  'Reduced risk with measurable security controls',
  'Fewer surprise failures through standards and lifecycle planning',
  'Procurement and deployment handled by one accountable team',
  'Clear ownership across vendors and platforms'
];

const operationSteps = [
  {
    number: 1,
    title: 'Assess',
    description: 'Document assets, risks, and priorities'
  },
  {
    number: 2,
    title: 'Stabilize',
    description: 'Address recurring issues and remove single points of failure'
  },
  {
    number: 3,
    title: 'Secure',
    description: 'Protect identity, endpoints, email, and backups. Verify recovery.'
  },
  {
    number: 4,
    title: 'Standardize',
    description: 'Establish baselines for devices, patching, onboarding, and offboarding'
  },
  {
    number: 5,
    title: 'Support & Improve',
    description: 'Ongoing monitoring, reporting, and continual refinement'
  }
];

const HomePage = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    
    const handleMediaChange = (e) => {
      setIsMobile(!e.matches);
    };

    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  return (
    <div className="relative overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative hero-gradient section-padding-home-hero" data-testid="hero-section">
        {/* Abstract shapes */}
        <div className="abstract-shape abstract-shape-1"></div>
        <div className="abstract-shape abstract-shape-2"></div>
        
        <div className="site-container relative">
          <div className="hero-copy">
            <h1 className="hero-headline text-3xl sm:text-4xl lg:text-5xl xl:text-[3.25rem] leading-[1.15] mb-5">
              Depend on NPM Computer Sales & Service for all your computer infrastructure needs.
            </h1>
            <p className="hero-lead text-lg lg:text-xl mb-5 leading-relaxed">
              We sell, install, secure, and support business technology across Las Vegas and Henderson, from workstations and servers to networks, Microsoft 365, cybersecurity, and remote support.
            </p>
            <p className="text-base font-medium text-[#7dc242] mb-6">
              Serving Southern Nevada since 1999.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="lg" 
                asChild 
                className="bg-[#7dc242] hover:bg-[#6ba83a] hover:shadow-lg hover:-translate-y-0.5 text-white border-0 h-11 px-6 text-[0.9375rem] transition-all"
                data-testid="hero-cta-phone"
              >
                <a href="tel:+17027695422">
                  <Phone className="w-[18px] h-[18px] mr-2" />
                  Call (702) 769-5422
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                asChild 
                className="border-2 border-gray-500 text-gray-600 hover:border-[#7dc242] hover:text-[#5a9e32] hover:bg-[#7dc242]/5 h-11 px-6 text-[0.9375rem] transition-all"
                data-testid="hero-cta-email"
              >
                <a href="mailto:david@applevar.com">
                  <Mail className="w-[18px] h-[18px] mr-2" />
                  Email Us
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section 
        className="section-padding section-bg-wrapper mobile-bg-green" 
        data-testid="services-section"
      >
        {/* Background layer - contained within section */}
        {!isMobile && (
          <div 
            className="section-bg-layer"
            style={{ backgroundImage: 'url(/assets/home-bg.jpg)' }}
            aria-hidden="true"
          />
        )}
        <div className="site-container section-bg-content">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              Core Services
            </h2>
            <p className="text-lg lg:text-xl text-black max-w-2xl mx-auto">
              Comprehensive IT solutions for your business.
            </p>
          </div>
          
          {/* Service Cards Grid - 3 columns desktop */}
          <div className="card-grid-services">
            {coreServices.map((service, index) => (
              <div 
                key={index} 
                className="npm-card service-card p-5 lg:p-6" 
                data-testid={`service-card-${index}`}
              >
                <div className="npm-card-icon mb-4">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="npm-card-title">
                  {service.title}
                </h3>
                <p className="npm-card-desc text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          {/* Partners line */}
          <p className="mt-10 text-center text-base text-black font-medium">
            Partnerships include Cisco, Microsoft, Xerox, Intel, and Lenovo.
          </p>

          <div className="mt-8 text-center">
            <Button 
              variant="outline" 
              asChild 
              className="bg-white border-2 border-gray-500 text-gray-600 hover:border-[#7dc242] hover:text-[#5a9e32] hover:bg-white/75 transition-all"
              data-testid="view-all-services"
            >
              <Link to="/services">
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="section-padding bg-white" data-testid="benefits-section">
        <div className="site-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-5">
                What You Get
              </h2>
              <p className="text-lg lg:text-xl text-gray-500 mb-6">
                Partner with NPM for reliable IT support that keeps your business running smoothly.
              </p>
              <ul className="space-y-5">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`benefit-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-[#7dc242] mt-0.5 flex-shrink-0" />
                    <span className="text-[#3f3f3f] text-[0.9375rem] leading-loose">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* NPM Monitoring Workstation */}
            <div className="relative">
              <div className="max-w-xl mx-auto">
                <img 
                  src="/assets/npm-workstation.png" 
                  alt="IT Monitoring Workstation - Multi-monitor setup displaying security dashboards and network topology" 
                  className="w-full h-auto object-contain rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Home Automation & Surveillance Section */}
      <section className="section-padding grid-pattern" data-testid="automation-section">
        <div className="site-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image on left for desktop */}
            <div className="relative order-2 lg:order-1">
              <div className="max-w-lg mx-auto">
                <img 
                  src="/assets/npm-automation-products.png" 
                  alt="Home Automation & Surveillance Products - Monitors, Cameras, Audio Systems, and Network Equipment" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            
            {/* Content on right */}
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-5">
                Home Automation & Surveillance
              </h2>
              <p className="text-lg lg:text-xl text-[#7D77B7] font-medium mb-4">
                Smart, secure solutions for homes and businesses.
              </p>
              <p className="text-[#3f3f3f] text-[0.9375rem] leading-relaxed">
                NPM provides integrated home automation, audio and video, access control, and surveillance solutions from trusted leading brands. We help you choose the right products, coordinate deployment, and create systems that are easy to use, reliable, and built for your space.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Operate Section */}
      <section className="section-padding grid-pattern" data-testid="operation-section">
        <div className="site-container">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              How We Operate
            </h2>
            <p className="text-lg lg:text-xl text-gray-500 max-w-2xl mx-auto">
              Our proven process ensures comprehensive IT management
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {operationSteps.map((step, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-200"
                  data-testid={`operation-step-${index}`}
                >
                  <div className="step-number">
                    {step.number}
                  </div>
                  <div className="pt-1">
                    <h3 className="text-base font-semibold text-[#3f3f3f] mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cta-section" data-testid="cta-section">
        <div className="site-container text-center">
          <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
            Ready to Get Started?
          </h2>
          <p className="text-lg lg:text-xl text-gray-500 mb-6 max-w-2xl mx-auto">
            Schedule an assessment to discuss your IT needs and how NPM can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              asChild 
              className="bg-[#7dc242] hover:bg-[#6ba83a] hover:shadow-lg hover:-translate-y-0.5 text-white border-0 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="cta-phone"
            >
              <a href="tel:+17027695422">
                <Phone className="w-[18px] h-[18px] mr-2" />
                Call (702) 769-5422
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              asChild 
              className="border-2 border-gray-500 text-gray-600 hover:border-[#7dc242] hover:text-[#5a9e32] hover:bg-[#7dc242]/5 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="cta-email"
            >
              <a href="mailto:david@applevar.com">
                <Mail className="w-[18px] h-[18px] mr-2" />
                david@applevar.com
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
